import { Injectable } from "@angular/core";
import { ServiceMessageBrokerFactory } from "shared/provider-webworker";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { MappedCurrencyPairCalendarImpl } from "../../../model/mapped/MappedCurrencyPairCalendarImpl";
import { CubiqWorker } from "../../../worker/CubiqWorker";
import { IBenchmarkFixingCalendarLookupService } from "shared/cubiq-services/lookup/model/IFixingCalendarLookupService";
import { CurrencyPairCalendarPromiseLookupService } from "shared/cubiq-services/lookup/model/impl/CurrencyPairCalendarPromiseLookupService";

/**
 * Service for retrieving a CurrencyPairCalendar from the server, converting it to a MappedCurrencyPairCalendar
 * and returning.
 * It has been agreed that this service currently does not cache (first checkin did have caching code if needed) so a single BM
 * order entry will generate a server request for each Pair/Fixing Date/Fixing Time combo selected.
 *
 */
@Injectable()
export class BenchmarkFixingCalendarLookupService implements IBenchmarkFixingCalendarLookupService {
  constructor(
    protected cubiqWorker: CubiqWorker,
    protected calendarService: CurrencyPairCalendarPromiseLookupService,
  ) {}

  /**
   * Get the set of tenors for the specified fixing date. If date or time is null  then Spot tenors for now
   * will be returned
   * @param base
   * @param nonBase
   * @param fixingDate If
   * @param fixingTime
   */
  public getFixingCalendar(
    base: string,
    nonBase: string,
    fixingDate: CubiqTS.StandardDate,
    fixingTime: CubiqTS.StandardTime,
  ): Promise<MappedCurrencyPairCalendarImpl> {
    // If we have no fixing date, then use use the current cached calendar for NOW
    if (fixingDate == null || fixingTime == null) {
      return this.calendarService.getCalendarWithHolidays(
        base,
        nonBase,
        CubiqTS.Product.FWD,
      ) as Promise<MappedCurrencyPairCalendarImpl>;
    }

    // This needs to be changed to reflect the fixing date and time
    let request = new CubiqTS.HolidayCalendarRequestImpl();
    request.baseCcyCd = base;
    request.nonBaseCcyCd = nonBase;
    request.product = CubiqTS.Product.FWD;
    request.relativeToDate = fixingDate;
    request.relativeToTime = fixingTime;
    const pairID = base + nonBase;

    let fixingCalendar = new Promise<MappedCurrencyPairCalendarImpl>((res, rej) => {
      this.cubiqWorker.isAuth().then(
        () => {
          return this.cubiqWorker.postServiceRequest(request).then(
            (data) => {
              logger.out.info("BM Calendar retrieved from server: " + pairID + fixingDate);
              if (data instanceof CubiqTS.CurrencyPairCalendarImpl) {
                const mapped = this.createMappedCurrencyPairImpl(data);
                res(mapped);
              } else {
                let reason = new Error(
                  `${this.constructor.name}.getFixingCalendar() invalid response`,
                );
                logger.out.error(reason);
                rej(reason);
              }
            },
            (err) => rej(err),
          );
        },
        (reason) => {
          logger.out.error(`${this.constructor.name}.getFixingCalendar()`);
          logger.out.error(reason);
          rej(reason);
        },
      );
    });
    return fixingCalendar;
  }

  /**
   * Create the MappedCurrencyPairCalendarImpl model that's used by the ValueDate control
   * @param data
   * @returns
   */
  private createMappedCurrencyPairImpl(data: CubiqTS.CurrencyPairCalendarImpl) {
    const mapped = new MappedCurrencyPairCalendarImpl();
    mapped.baseCcy = data.baseCcy;
    mapped.nonBaseccy = data.nonBaseccy;
    mapped.tradeDateRollDate = data.tradeDateRollDate;
    mapped.tradeDateRollTime = data.tradeDateRollTime;
    mapped.tenorToValueDateSet = data.tenorToValueDateSet;
    mapped.holidays = data.holidays;
    let tenorToValueDateMap = new Map();
    if (mapped.tenorToValueDateSet) {
      mapped.tenorToValueDateSet.sort((a, b) => a.tenor - b.tenor);
      for (let tenorToValue of mapped.tenorToValueDateSet) {
        tenorToValueDateMap.set(tenorToValue.tenor, tenorToValue);
      }
    }
    mapped.tenorToValueDateMap = tenorToValueDateMap;
    return mapped;
  }
}
